import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Mail, Phone, DollarSign, Tag, Plus, Building2, Settings2, EyeOff, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import { mortgageApi } from '../workspacesApi';
import { useAuth } from '../AuthContext';
import { AssignmentPicker } from '../loan-file/shared/AssignmentPicker';
import { uc } from '../unifiedContactsApi';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import Board from './Board';
import BoardCard from './BoardCard';
import CardDrawer from './CardDrawer';
import GhlBadge from './GhlBadge';
import AriveBadge from './AriveBadge';
import AlertsStrip from './AlertsStrip';
import CreateOpportunityDialog from './CreateOpportunityDialog';
import OpportunityEditSection from './OpportunityEditSection';
import { GHL_LOCKED_FIELDS, ARIVE_LOCKED_FIELDS, lenderName } from './oppFields';
import { formatDate, fromApi } from '../kit/time';
import StageConfigDialog from './StageConfigDialog';
import LenderPanel from './LenderPanel';
import LoadMoreBar from './LoadMoreBar';
import { PAGE_LIMIT, appendNew, firstPage, nextPage } from './paging';
import { addressLine } from '../shared/address';
import { formatPhone } from '../shared/phone';
import { OPP_LOAN_TYPE_LABELS, OPP_LOAN_PURPOSE_LABELS, labelFor } from '../shared/labels';

const ALERT_DAYS = 14;
const fmt$ = (n) => n ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '—';
const primary = (list) => (list || []).find(x => x.is_primary)?.value || list?.[0]?.value || null;
const byOrder = (list) => (list || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const syncMinutesAgo = (iso) => Math.max(0, Math.round((Date.now() - fromApi(iso).getTime()) / 60000));

const cardId = (o) => o.opportunity_id;
const cardStage = (o) => o.stage;

// Ticket 122 S6 — the active GHL ref on an opportunity, when it has one.
const ghlRef = (o) => (o.external_refs || []).find(r => r.system === 'ghl');
// Ticket 134 P2 — a card that came from an Arive lead carries the lead mirror.
const ariveLead = (o) => (o.arive && o.arive.lead_id ? o.arive : null);
// Stale only while it is still an unconverted, pre-application lead: a loan or a
// later stage takes a card out of the stale group.
// "Pre-application" = every tenant stage ordered before application_started.
const DEFAULT_PRE_APPLICATION_STAGES = ['new_lead', 'attempted_contact', 'contacted'];
const preApplicationStages = (stages) => {
  const sorted = byOrder(stages);
  const at = sorted.findIndex(s => s.key === 'application_started');
  return at < 0 ? DEFAULT_PRE_APPLICATION_STAGES : sorted.slice(0, at).map(s => s.key);
};
const isStaleArive = (o, preStages) =>
  !!o.arive?.stale && !o.arive?.converted && preStages.includes(o.stage);
const countStages = (list) => {
  const n = {};
  for (const o of list) n[o.stage] = (n[o.stage] || 0) + 1;
  return n;
};
const CLOSED_LABELS = { won: 'Won', lost: 'Lost', abandoned: 'Abandoned' };
// BFY-native closes store the terminal stage key; fold them onto GHL's vocabulary.
const BFY_CLOSED_TO_GHL = { funded: 'won', denied: 'lost', withdrawn: 'abandoned' };
const closedStatus = (o) => o.ghl?.status || BFY_CLOSED_TO_GHL[o.closed_reason] || o.closed_reason;
const dateStr = (iso) => (iso ? formatDate(iso) : '—');

// Ticket 187 — what the GHL / Arive guards own on a mirrored card, for the edit form. GHL locks only
// while the card is pre-application (the server decides the same way); Arive locks for the card's life.
// `note` is the line shown beside the locked fields, with the link out. The stage select and the
// Loan Officer slot are locked on the same terms (the stage-move and assignment routes 409): `stageLocked`
// for GHL pre-application or any Arive card, `loLocked` for GHL pre-application only.
const mirrorLock = (o, preStages) => {
  if (o.ghl_managed) {
    const pre = preStages.includes(o.stage);
    return {
      locked: pre ? GHL_LOCKED_FIELDS : [],
      note: { text: 'Managed in GoHighLevel', href: o.ghl_edit_url, link: 'Edit in GHL' },
      stageLocked: pre,
      loLocked: pre,
    };
  }
  if (o.arive_managed) {
    return {
      locked: ARIVE_LOCKED_FIELDS,
      note: { text: 'Managed in Arive', href: o.arive_edit_url, link: 'Open in Arive' },
      stageLocked: true,
      loLocked: false,
    };
  }
  return { locked: [], note: null, stageLocked: false, loLocked: false };
};

// Card assignments (ticket 69) — the loan-header picker pointed at the opportunity.
const oppAssignments = {
  list: (id) => mortgageApi.listAssignments(id),
  add: (id, body) => mortgageApi.addAssignment(id, body),
  remove: (id, userId, slot) => mortgageApi.removeAssignment(id, userId, slot),
};

// Mortgage flavor of the shared Board: columns from the tenant's v2 stage
// config, cards from mortgage_opportunities, borrower names from one batch
// contacts_v2 lookup per load. Also hosts the alerts strip, lender panel,
// create modal and stage-config editor (from the retired Mortgage Workspace page).
export default function MortgageBoard({ initialOpenId, newForContactId } = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAssign = (user?.permissions || []).includes('loans.assign');
  const canConfigure = user?.is_super_admin || (user?.permissions || []).includes('pipeline.configure');
  const [stages, setStages] = useState([]);          // full list incl. hidden, sorted
  const [opps, setOpps] = useState([]);
  const [contacts, setContacts] = useState({});      // contact_id -> contact
  const [alerts, setAlerts] = useState(null);
  const [lenders, setLenders] = useState([]);
  const [sync, setSync] = useState(null);              // GHL sync status (ticket 122 S5), null when unavailable
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);        // drawer open request (alerts)
  const initialOpenApplied = useRef(false);
  const [creating, setCreating] = useState(false);
  const [presetContact, setPresetContact] = useState(null);   // contact the create dialog starts with
  const newForApplied = useRef(false);
  const [configuring, setConfiguring] = useState(false);
  const [lendersOpen, setLendersOpen] = useState(false);
  const [closedFilter, setClosedFilter] = useState('open');   // ticket 122 S6
  const [closedOpps, setClosedOpps] = useState([]);
  const [closedContacts, setClosedContacts] = useState({});
  const [closedLoading, setClosedLoading] = useState(false);
  const [page, setPage] = useState({ loaded: 0, total: 0, cursor: null });             // open list paging (ticket 187)
  const [closedPage, setClosedPage] = useState({ loaded: 0, total: 0, cursor: null }); // closed list paging
  const [loadingMore, setLoadingMore] = useState(false);
  const [pinned, setPinned] = useState(null);   // a closed card opened by link, shown even when the closed page lacks it

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [stageDoc, oppDoc, alertDoc, lenderList, syncDoc] = await Promise.all([
        mortgageApi.getStages(),
        mortgageApi.listOpps({ limit: PAGE_LIMIT }),
        mortgageApi.alerts(ALERT_DAYS).catch(() => null),
        mortgageApi.listLenders().catch(() => []),
        mortgageApi.syncStatus().catch(() => null),
      ]);
      const items = oppDoc.items || [];
      const ids = [...new Set(items.map(o => o.contact_id).filter(Boolean))];
      const people = await uc.byIds(ids).catch(() => []);
      setStages(byOrder(stageDoc.stages));
      setOpps(items);
      setPage(firstPage(items, oppDoc));
      setContacts(Object.fromEntries(people.map(p => [p.contact_id, p])));
      setAlerts(alertDoc);
      setLenders(lenderList || []);
      setSync(syncDoc);
    } catch (e) {
      toast({ title: 'Load failed', description: e?.response?.data?.detail || String(e) });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Closed-deal filter (ticket 122 S6) — fetched separately since the open
  // board list already excludes closed opportunities. The server buckets by `closed_status`, so
  // `total`, the paging and the rows all describe the selected view.
  useEffect(() => {
    if (closedFilter === 'open') return;
    let cancelled = false;
    setClosedLoading(true);
    mortgageApi.listOpps({ limit: PAGE_LIMIT, closed_status: closedFilter })
      .then(async (doc) => {
        const items = doc.items || [];
        const ids = [...new Set(items.map(o => o.contact_id).filter(Boolean))];
        const people = await uc.byIds(ids).catch(() => []);
        if (cancelled) return;
        setClosedOpps(items);
        setClosedPage(firstPage(items, doc));
        setClosedContacts(Object.fromEntries(people.map(p => [p.contact_id, p])));
      })
      .catch((e) => {
        if (!cancelled) toast({ title: 'Load failed', description: e?.response?.data?.detail || String(e) });
      })
      .finally(() => { if (!cancelled) setClosedLoading(false); });
    return () => { cancelled = true; };
  }, [closedFilter]);

  const allContacts = useMemo(() => ({ ...contacts, ...closedContacts }), [contacts, closedContacts]);
  const closedItems = useMemo(() => {
    const list = closedOpps.filter(o => o.is_closed && closedStatus(o) === closedFilter);
    return pinned && pinned.is_closed && closedStatus(pinned) === closedFilter ? appendNew(list, [pinned]) : list;
  }, [closedOpps, closedFilter, pinned]);
  const closedColumns = useMemo(
    () => [{ key: 'closed', label: CLOSED_LABELS[closedFilter] || closedFilter, color: '#94A3B8' }],
    [closedFilter],
  );

  // Open a card's drawer by id (alerts strip, deep link). A card the open list does not hold is
  // fetched: an open one (past the page cap) joins the board; a closed one switches to its closed
  // view, pinned so it shows even when the first page of closed cards does not include it.
  const openById = useCallback(async (id) => {
    if (opps.some(o => o.opportunity_id === id)) { setOpenId(id); return; }
    try {
      const o = await mortgageApi.getOpp(id);
      if (o.contact_id && !contacts[o.contact_id]) {
        const people = await uc.byIds([o.contact_id]).catch(() => []);
        if (people[0]) setContacts(prev => ({ ...prev, [people[0].contact_id]: people[0] }));
      }
      if (!o.is_closed) {
        setOpps(prev => appendNew(prev, [o]));
        setOpenId(id);
        return;
      }
      const key = closedStatus(o);
      if (!CLOSED_LABELS[key]) throw new Error('no closed view for this status');
      setPinned(o);
      setClosedFilter(key);
      setOpenId(id);
    } catch {
      toast({ title: 'Not on the board', description: 'That opportunity is no longer in the pipeline.' });
    }
  }, [opps, contacts]);

  // Deep link from a contact's opportunity list (PipelinePage's initialOpenId) —
  // apply once, after the initial load, so a missing card gets a single toast.
  useEffect(() => {
    if (!initialOpenId || initialOpenApplied.current || loading) return;
    initialOpenApplied.current = true;
    openById(initialOpenId);
  }, [initialOpenId, loading, openById]);

  // "New opportunity" link on a contact's profile (PipelinePage's newForContactId): open the create
  // dialog with that contact picked. If the contact can't be read the dialog opens blank.
  useEffect(() => {
    if (!newForContactId || newForApplied.current) return;
    newForApplied.current = true;
    (async () => {
      try {
        const c = await uc.get(newForContactId);
        setPresetContact({ contact_id: c.contact_id, display_name: c.display_name });
      } catch {
        toast({ title: 'Contact not found', description: 'Pick the contact in the dialog instead.' });
      }
      setCreating(true);
    })();
  }, [newForContactId]);

  const columns = useMemo(() => stages.filter(s => !s.hidden), [stages]);
  // Stale Arive leads (created before 2025) sit in their own collapsed group.
  const preStages = useMemo(() => preApplicationStages(stages), [stages]);
  const freshOpps = useMemo(() => opps.filter(o => !isStaleArive(o, preStages)), [opps, preStages]);
  const staleOpps = useMemo(() => opps.filter(o => isStaleArive(o, preStages)), [opps, preStages]);
  const [staleOpen, setStaleOpen] = useState(false);
  useEffect(() => {
    if (openId && staleOpps.some(o => o.opportunity_id === openId)) setStaleOpen(true);
  }, [openId, staleOpps]);
  // Cards hidden in the stale group are not on the board, so they are not counted
  // against a hidden stage; the stage editor still counts every card.
  const stageCounts = useMemo(() => countStages(freshOpps), [freshOpps]);
  const allStageCounts = useMemo(() => countStages(opps), [opps]);
  const hiddenWithCards = useMemo(
    () => stages.filter(s => s.hidden && stageCounts[s.key]).map(s => ({ ...s, count: stageCounts[s.key] })),
    [stages, stageCounts],
  );

  const nameOf = useCallback((o) => allContacts[o.contact_id]?.display_name || 'Unknown borrower', [allContacts]);
  // Persist the move, then mirror it into `opps`: a later edit re-seeds the Board's placement from
  // `opps`, which would otherwise snap a moved card back, and the edit form's GHL lock reads the stage.
  const onMove = useCallback(async (o, toStage) => {
    const r = await mortgageApi.moveStage(o.opportunity_id, toStage);
    setOpps(prev => prev.map(x => x.opportunity_id === o.opportunity_id ? { ...x, stage: toStage } : x));
    return r;
  }, []);

  // Edit saved from the drawer: fold the saved fields into whichever list holds the card (the PATCH
  // reply is undecorated, so the GHL/Arive flags already on the card are kept) and refresh the alerts,
  // since the form is what sets their dates.
  const onOppSaved = useCallback((updated) => {
    const merge = (list) => list.map(x => x.opportunity_id === updated.opportunity_id ? { ...x, ...updated } : x);
    setOpps(merge);
    setClosedOpps(merge);
    setPinned(p => (p && p.opportunity_id === updated.opportunity_id ? { ...p, ...updated } : p));
    mortgageApi.alerts(ALERT_DAYS).then(setAlerts).catch(() => {});
  }, []);

  // Create: prepend the new card and make sure its borrower name resolves
  // without reloading the whole board.
  const onCreated = useCallback(async (opp, contact) => {
    setOpps(prev => [opp, ...prev]);
    setPage(p => ({ ...p, loaded: p.loaded + 1, total: p.total + 1 }));
    if (contact) {
      setContacts(prev => ({ ...prev, [contact.contact_id]: contact }));
    } else if (opp.contact_id && !contacts[opp.contact_id]) {
      const people = await uc.byIds([opp.contact_id]).catch(() => []);
      if (people[0]) setContacts(prev => ({ ...prev, [people[0].contact_id]: people[0] }));
    }
  }, [contacts]);

  const saveStages = useCallback(async (next) => {
    const doc = await mortgageApi.setStages(next);
    setStages(byOrder(doc.stages));
    return doc;
  }, []);

  const onLenderAdded = useCallback((l) => {
    setLenders(prev => [...prev, l].sort((a, b) => a.name.localeCompare(b.name)));
  }, []);
  const onLenderUpdated = useCallback((l) => {
    setLenders(prev => prev.map(x => x.lender_id === l.lender_id ? l : x).sort((a, b) => a.name.localeCompare(b.name)));
  }, []);
  const onLenderDeleted = useCallback((id) => {
    setLenders(prev => prev.filter(x => x.lender_id !== id));
  }, []);

  // Next page of the open or closed list, with the borrower names it needs.
  const loadMore = useCallback(async () => {
    const closed = closedFilter !== 'open';
    const cur = closed ? closedPage : page;
    setLoadingMore(true);
    try {
      const doc = await mortgageApi.listOpps({ limit: PAGE_LIMIT, cursor: cur.cursor, ...(closed ? { closed_status: closedFilter } : {}) });
      const items = doc.items || [];
      const known = closed ? allContacts : contacts;
      const ids = [...new Set(items.map(o => o.contact_id).filter(id => id && !known[id]))];
      const people = await uc.byIds(ids).catch(() => []);
      const byId = Object.fromEntries(people.map(p => [p.contact_id, p]));
      if (closed) {
        setClosedOpps(prev => appendNew(prev, items));
        setClosedContacts(prev => ({ ...prev, ...byId }));
        setClosedPage(nextPage(cur, items, doc));
      } else {
        setOpps(prev => appendNew(prev, items));
        setContacts(prev => ({ ...prev, ...byId }));
        setPage(nextPage(cur, items, doc));
      }
    } catch (e) {
      toast({ title: 'Load failed', description: errorMessage(e) });
    } finally { setLoadingMore(false); }
  }, [closedFilter, closedPage, page, allContacts, contacts]);

  const renderCard = useCallback((o) => {
    const ref = ghlRef(o);
    return (
      <BoardCard
        product="mortgage"
        avatar={nameOf(o)[0]?.toUpperCase()}
        title={nameOf(o)}
        subtitle={addressLine(o.property_address)}
        footerLeft={o.credit_score ? `FICO ${o.credit_score}` : labelFor(OPP_LOAN_TYPE_LABELS, o.loan_type)}
        footerRight={fmt$(o.loan_amount)}
        badge={(ref || o.arive_link || ariveLead(o)) ? (
          <span className="inline-flex items-center gap-1">
            {ref ? <GhlBadge detached={ref.status === 'detached'} /> : null}
            {(o.arive_link || ariveLead(o)) ? <AriveBadge /> : null}
          </span>
        ) : null}
      />
    );
  }, [nameOf]);

  const renderDrawer = useCallback(({ card: o, stage, onClose, onChangeStage }) => {
    const c = allContacts[o.contact_id];
    const email = primary(c?.emails);
    const phone = primary(c?.phones);
    const ref = ghlRef(o);
    const lead = ariveLead(o);
    const mirror = mirrorLock(o, preStages);
    return (
      <CardDrawer
        eyebrow="Opportunity"
        title={nameOf(o)}
        amountLabel="Loan Amount"
        amount={o.loan_amount}
        columns={columns}
        currentStage={stage}
        onChangeStage={onChangeStage}
        onClose={onClose}
        stageLock={mirror.stageLocked ? mirror.note : null}
        openFull={o.loan_file_id ? { label: 'Open full record', onClick: () => navigate(`/portal/loans/${o.loan_file_id}`) } : null}
      >
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Contact</div>
          {email && <div className="flex items-center gap-2 text-sm text-slate-700 mb-1" data-testid="drawer-contact-email"><Mail size={13} className="text-slate-400" /> {email}</div>}
          {phone && <div className="flex items-center gap-2 text-sm text-slate-700" data-testid="drawer-contact-phone"><Phone size={13} className="text-slate-400" /> {formatPhone(phone)}</div>}
          {!email && !phone && <div className="text-sm text-slate-400 italic">No contact info yet.</div>}
        </div>
        <OpportunityEditSection kind="mortgage" opp={o} lenders={lenders} locked={mirror.locked} lockNote={mirror.note} onSaved={onOppSaved}>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2 text-slate-700"><DollarSign size={13} className="text-slate-400" /> Type: <b style={{ color: '#070B5F' }}>{labelFor(OPP_LOAN_TYPE_LABELS, o.loan_type) || '—'}</b></div>
            <div className="flex items-center gap-2 text-slate-700"><Tag size={13} className="text-slate-400" /> Purpose: <b style={{ color: '#070B5F' }}>{labelFor(OPP_LOAN_PURPOSE_LABELS, o.loan_purpose) || '—'}</b></div>
            {addressLine(o.property_address) && <div className="text-slate-700">Property: <b style={{ color: '#070B5F' }}>{addressLine(o.property_address)}</b></div>}
            {o.purchase_price ? <div className="text-slate-700">Purchase price: <b style={{ color: '#070B5F' }}>{fmt$(o.purchase_price)}</b></div> : null}
            {typeof o.credit_score === 'number' && <div className="text-slate-700">FICO: <b style={{ color: '#070B5F' }}>{o.credit_score}</b></div>}
            {o.lender_id && <div className="text-slate-700" data-testid="drawer-lender">Lender: <b style={{ color: '#070B5F' }}>{lenderName(lenders, o.lender_id)}</b></div>}
            {(o.rate_lock?.rate != null || o.rate_lock?.expires_at) && (
              <div className="text-slate-700" data-testid="drawer-rate-lock">Rate lock: <b style={{ color: '#070B5F' }}>{[o.rate_lock.rate != null ? `${o.rate_lock.rate}%` : null, o.rate_lock.expires_at ? `expires ${dateStr(o.rate_lock.expires_at)}` : null].filter(Boolean).join(' · ')}</b></div>
            )}
            {o.preapproval_expires_at && <div className="text-slate-700">Preapproval expires: <b style={{ color: '#070B5F' }}>{dateStr(o.preapproval_expires_at)}</b></div>}
            {o.estimated_closing_date && <div className="text-slate-700">Est. closing: <b style={{ color: '#070B5F' }}>{dateStr(o.estimated_closing_date)}</b></div>}
            {o.notes && <div className="text-slate-700 whitespace-pre-wrap" data-testid="drawer-notes">Notes: <b style={{ color: '#070B5F' }}>{o.notes}</b></div>}
          </div>
        </OpportunityEditSection>
        {lead && (
          <div data-testid="drawer-arive">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Arive</div>
            <div className="space-y-1 text-sm">
              <AriveBadge deepLink={o.arive_edit_url || null} />
              <div className="text-slate-700">Lead status: <b style={{ color: '#070B5F' }}>{lead.status || '—'}</b></div>
              {lead.source && <div className="text-slate-700">Source: <b style={{ color: '#070B5F' }}>{lead.source}</b></div>}
              {lead.converted && <div className="text-slate-700">Converted to a loan in Arive.</div>}
              {lead.needs_assignment && <div className="text-amber-700">No Arive assignee matched a team member: assign it below.</div>}
            </div>
          </div>
        )}
        {ref && (
          <div data-testid="drawer-ghl">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">GoHighLevel</div>
            {ref.status === 'detached' ? (
              <div className="text-sm text-slate-500 italic">GHL link detached — this record is no longer in GoHighLevel.</div>
            ) : (
              <div className="space-y-1 text-sm">
                <div className="text-slate-700">Status: <b style={{ color: '#070B5F' }}>{o.ghl?.status ? o.ghl.status[0].toUpperCase() + o.ghl.status.slice(1) : '—'}</b></div>
                <div className="text-slate-700">Value: <b style={{ color: '#070B5F' }}>{fmt$(o.ghl?.monetary_value)}</b></div>
                <div className="text-slate-700">Opportunity name: <b style={{ color: '#070B5F' }}>{o.ghl?.name || '—'}</b></div>
                <div className="text-slate-700">Last stage change: <b style={{ color: '#070B5F' }}>{dateStr(o.ghl?.last_stage_change_at)}</b></div>
                <div className="text-slate-700">Updated: <b style={{ color: '#070B5F' }}>{dateStr(o.ghl?.date_updated)}</b></div>
                <div className="pt-2">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Notes</div>
                  {(o.ghl?.notes || []).length === 0 ? (
                    <div className="text-slate-400 italic">No GHL notes.</div>
                  ) : (
                    <div className="space-y-2" data-testid="drawer-ghl-notes">
                      {[...o.ghl.notes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((n) => (
                        <div key={n.id} className="text-slate-700">
                          <div>{n.body}</div>
                          <div className="text-[11px] text-slate-400">{dateStr(n.created_at)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {o.ghl_edit_url && (
                  <a href={o.ghl_edit_url} target="_blank" rel="noreferrer" data-testid="drawer-ghl-edit"
                    className="inline-block mt-2 text-sm font-semibold underline" style={{ color: '#070B5F' }}>
                    Edit in GHL
                  </a>
                )}
              </div>
            )}
          </div>
        )}
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Team</div>
          <AssignmentPicker loanId={o.opportunity_id} client={oppAssignments} canAssign={canAssign} title="Assign to this deal"
            lockedSlot={mirror.loLocked ? { slot: 'loan_officer', note: { ...mirror.note, text: 'Loan Officer: managed in GoHighLevel' } } : null} />
        </div>
      </CardDrawer>
    );
  }, [allContacts, columns, nameOf, navigate, canAssign, lenders, preStages, onOppSaved]);

  const alertLabel = useCallback((i) => (i.contact_id ? contacts[i.contact_id]?.display_name : null), [contacts]);

  if (loading) {
    return (
      <div className="p-16 flex items-center justify-center text-slate-400" data-testid="pipeline-loading">
        <Loader2 className="animate-spin mr-2" size={20} /> Loading pipeline...
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-end gap-2 mb-4" data-testid="pipeline-toolbar">
        {sync && sync.enabled && (
          <span
            data-testid="ghl-sync-status"
            className="text-[11px] font-semibold px-2 py-1 rounded-full mr-auto"
            title={sync.last_ok_at || ''}
            style={sync.stale
              ? { background: '#FEF3C7', color: '#92400E' }
              : { background: 'transparent', color: '#94A3B8' }}
          >
            {sync.last_ok_at ? `GHL synced ${syncMinutesAgo(sync.last_ok_at)}m ago` : 'GHL sync: never'}
          </span>
        )}
        <Select value={closedFilter} onValueChange={setClosedFilter}>
          <SelectTrigger className="h-9 w-40" data-testid="pipeline-closed-filter" aria-label="Closed deals">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open deals</SelectItem>
            <SelectItem value="won">Won</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
            <SelectItem value="abandoned">Abandoned</SelectItem>
          </SelectContent>
        </Select>
        <button onClick={() => setLendersOpen(true)} className="btn-outline" data-testid="pipeline-lenders-btn">
          <Building2 size={14} /> Lenders ({lenders.length})
        </button>
        {canConfigure && (
          <button onClick={() => setConfiguring(true)} className="btn-outline" data-testid="pipeline-stages-btn">
            <Settings2 size={14} /> Configure stages
          </button>
        )}
        <button onClick={() => setCreating(true)} className="btn-gold" data-testid="pipeline-new-opp-btn">
          <Plus size={16} /> New Opportunity
        </button>
      </div>

      {closedFilter === 'open' && (
        <>
          <AlertsStrip alerts={alerts} days={ALERT_DAYS} product="mortgage" labelFor={alertLabel} onOpen={(i) => openById(i.opportunity_id)} />

          {hiddenWithCards.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 mb-4" data-testid="pipeline-hidden-notice">
              <EyeOff size={13} className="text-slate-400" />
              <span className="font-semibold">{hiddenWithCards.reduce((n, s) => n + s.count, 0)} in hidden stages:</span>
              {hiddenWithCards.map(s => (
                <span key={s.key} className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2 py-0.5" data-testid={`pipeline-hidden-${s.key}`}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />{s.label} · {s.count}
                </span>
              ))}
              {canConfigure && (
                <button onClick={() => setConfiguring(true)} className="underline text-slate-500 hover:text-[#070B5F]">edit stages</button>
              )}
            </div>
          )}
        </>
      )}

      {closedFilter === 'open' ? (
        <>
          <LoadMoreBar shown={page.loaded} total={page.total} hasMore={!!page.cursor} noun="open deals" loading={loadingMore} onMore={loadMore} />
          <Board
            columns={columns}
            cards={freshOpps}
            cardId={cardId}
            cardStage={cardStage}
            cardTitle={nameOf}
            onMove={onMove}
            renderCard={renderCard}
            renderDrawer={renderDrawer}
            emptyMsg="Drop opportunities here"
            testIdPrefix="pipeline"
            openId={openId}
            onOpenChange={setOpenId}
          />
          {staleOpps.length > 0 && (
            <div className="mt-2 mb-6" data-testid="pipeline-stale-group">
              <button
                type="button"
                onClick={() => setStaleOpen(v => !v)}
                aria-expanded={staleOpen}
                data-testid="pipeline-stale-toggle"
                className="flex items-center gap-2 text-sm font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 mb-3"
              >
                {staleOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                Stale Arive leads ({staleOpps.length})
              </button>
              {staleOpen && (
                <Board
                  columns={columns}
                  cards={staleOpps}
                  cardId={cardId}
                  cardStage={cardStage}
                  cardTitle={nameOf}
                  onMove={onMove}
                  renderCard={renderCard}
                  renderDrawer={renderDrawer}
                  emptyMsg="No stale leads"
                  testIdPrefix="pipeline-stale"
                  openId={openId}
                  onOpenChange={setOpenId}
                />
              )}
            </div>
          )}
        </>
      ) : closedLoading ? (
        <div className="p-16 flex items-center justify-center text-slate-400" data-testid="pipeline-closed-loading">
          <Loader2 className="animate-spin mr-2" size={20} /> Loading...
        </div>
      ) : (
        <>
          <LoadMoreBar shown={closedPage.loaded} total={closedPage.total} hasMore={!!closedPage.cursor} noun="closed deals" loading={loadingMore} onMore={loadMore} />
          <Board
            columns={closedColumns}
            cards={closedItems}
            cardId={cardId}
            cardStage={() => 'closed'}
            cardTitle={nameOf}
            onMove={async () => {}}
            renderCard={renderCard}
            renderDrawer={renderDrawer}
            emptyMsg={`No ${closedFilter} deals`}
            testIdPrefix="pipeline-closed"
            openId={openId}
            onOpenChange={setOpenId}
          />
        </>
      )}

      <CreateOpportunityDialog key={presetContact?.contact_id || 'blank'} initialContact={presetContact} open={creating} onOpenChange={setCreating} onCreated={onCreated} stages={columns} lenders={lenders} canAssign={canAssign} />
      <StageConfigDialog open={configuring} onOpenChange={setConfiguring} stages={stages} counts={allStageCounts} onSave={saveStages} />
      <LenderPanel open={lendersOpen} onClose={() => setLendersOpen(false)} lenders={lenders} canManage={canConfigure} onAdded={onLenderAdded} onUpdated={onLenderUpdated} onDeleted={onLenderDeleted} />
    </>
  );
}
